import { Expertises } from '@platform/client';
import {
  Dropdown,
  DropdownItem,
  MultiSelectCategory,
  MultiSelectCategoryDropdown,
  PillBadge,
} from '@platform/components';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../Application';
import { useApplicationLocalStorage } from '../application-hook';
import { ApplicationBackground } from '../ApplicationBackground';
import { ApplicationPageContent } from '../components/ApplicationPageContent';
import SkillsImage from '../images/Updatedcomposite2.svg';

type SkillExperience = {
  skill: string;
  experience?: string;
};

type FormValues = {
  skills: SkillExperience[];
};

const skillExperienceOptions = [
  { value: '0-1', label: 'Less than 2 years' },
  { value: '2-5', label: '2-5 years' },
  { value: '6-10', label: '6-10 years' },
  { value: '10+', label: 'More than 10 years' },
];

export const SkillsExperience = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress, onSaveExit, onSubmit, isEditProfile } = { ...props };
  const [submitted, setSubmitted] = useState(false);

  const { updateApplicationData, getApplicationDataByKey } = useApplicationLocalStorage();

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
    register,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { skills: getApplicationDataByKey('skills') },
  });

  const allSkillsHaveExperience = watch('skills')?.every((skill) => skill.experience);

  const submit: SubmitHandler<FormValues> = (data) => {
    if (!allSkillsHaveExperience) {
      setSubmitted(true);
      return;
    }

    updateApplicationData({ skills: data.skills });

    if (onSubmit) {
      onSubmit();
    } else {
      onContinue();
    }
  };

  const expertisesList = Expertises.useGetExpertises();

  const classificationOptions =
    expertisesList.isSuccess ?
      Array.from(
        expertisesList.data
          .reduce<Map<string, MultiSelectCategory>>((map, expertise) => {
            if (!map.has(expertise.classification)) {
              map.set(expertise.classification, {
                name: expertise.classification,
                options: [],
              });
            }
            /* eslint-disable-next-line */
            map.get(expertise.classification)!.options.push(expertise.name);
            return map;
          }, new Map())
          .values(),
      )
    : [];

  const onSkillsSelectionChange = (value: SkillExperience[]) => {
    setValue('skills', value);
    if (value.length > 0) {
      clearErrors('skills');
    }
  };

  const skills = watch('skills') || [];

  const handleBack = () => {
    updateApplicationData({
      skills: skills,
    });
    onBack();
  };

  return (
    <ApplicationBackground
      progress={progress}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      isEditProfile={isEditProfile}
      backgroundImage={SkillsImage}
      onSaveAndExit={onSaveExit}
    >
      <ApplicationPageContent
        title='Which skills would you like to offer organizations?'
        description="You bring unique skills - let's help you use them for impact. You'll be able to select your level of experience in each."
      >
        <MultiSelectCategoryDropdown
          categories={classificationOptions}
          label='Select up to 5 skills'
          selectionLimit={5}
          placeholder='Select skills'
          selectedItems={skills.map((value) => value.skill) || []}
          onSelectionChange={(value) => {
            const newValues: SkillExperience[] = [];
            for (const skill of value) {
              const skillExperience = skills.find(
                (skillExperience) => skillExperience.skill === skill,
              );
              if (skillExperience) {
                newValues.push({ skill, experience: skillExperience.experience });
              } else {
                newValues.push({ skill, experience: undefined });
              }
            }
            onSkillsSelectionChange(newValues);
          }}
          error={errors.skills?.message}
          register={() => {
            return register('skills', {
              required: 'Select at least one skill to continue',
            });
          }}
        />
        {skills?.length > 0 && (
          <div className='flex flex-col gap-4'>
            <div>How many years of experience do you have in each skill?</div>
            {skills.map((skill) => {
              return (
                <div className='flex flex-row justify-between' key={skill.skill}>
                  <PillBadge
                    key={skill.skill}
                    size='xl'
                    variant='close'
                    onClick={() => {
                      const updatedSkills = watch('skills').filter((s) => s.skill !== skill.skill);
                      onSkillsSelectionChange(updatedSkills);
                    }}
                    additionalClassnames='h-10'
                    color='gray'
                  >
                    {skill.skill}
                  </PillBadge>
                  <div className='flex flex-col gap-2'>
                    <Dropdown
                      type='button'
                      ariaLabel={`Select experience for ${skill}`}
                      buttonProps={{
                        variant: 'secondary-gray',
                        size: 'lg',
                        additionalClassnames: `bg-white text-md text-gray-700 font-normal! px-3 py-2 ${submitted && !skill.experience ? 'border-error-500' : ''}`,
                        children: (
                          <div>
                            {skillExperienceOptions.find(
                              (option) => option.value === skill.experience,
                            )?.label ?? 'Select years'}
                          </div>
                        ),
                      }}
                    >
                      {skillExperienceOptions.map((value) => {
                        return (
                          <DropdownItem
                            key={value.label}
                            onAction={() => {
                              const updatedSkills = watch('skills').map((s) => {
                                if (s.skill === skill.skill) {
                                  return { ...s, experience: value.value };
                                }
                                return s;
                              });
                              onSkillsSelectionChange(updatedSkills);
                              setSubmitted(false);
                            }}
                          >
                            {value.label}
                          </DropdownItem>
                        );
                      })}
                    </Dropdown>
                    {!skill.experience && submitted && (
                      <div className='text-error-500 text-sm'>Select years for this skill</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
